import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { resolvePostAuthPath } from '@/lib/post-auth-path';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  Mail,
  Eye,
  EyeOff,
} from 'lucide-react';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { AuthCharacters } from '@/components/auth/AuthCharacters';

type Tab = 'login' | 'signup';

export default function Auth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab: Tab = searchParams.get('tab') === 'signup' ? 'signup' : 'login';
  const { signIn, signUp, user, isLoading, isConsumer, isPrestador, needsOnboarding } = useAuth();
  const { track } = useAnalytics();

  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentToEmail, setSentToEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const emailSchema = useMemo(
    () => z.string().email(t('common.validation.invalidEmail')),
    [t],
  );
  const senhaSchema = useMemo(
    () => z.string().min(6, t('common.validation.passwordMinLength')),
    [t],
  );
  const nomeSchema = useMemo(
    () => z.string().min(2, t('common.validation.nameMinLength')),
    [t],
  );

  // Login state
  const [emailLogin, setEmailLogin] = useState('');
  const [senhaLogin, setSenhaLogin] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  // Signup state
  const [nomeSignup, setNomeSignup] = useState('');
  const [emailSignup, setEmailSignup] = useState('');
  const [senhaSignup, setSenhaSignup] = useState('');
  const [confirmaSenha, setConfirmaSenha] = useState('');

  const emailValidation = useMemo(() => {
    if (!emailLogin || !emailTouched) return { isValid: null as boolean | null, error: null as string | null };
    try {
      emailSchema.parse(emailLogin);
      return { isValid: true, error: null };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return { isValid: false, error: error.errors[0].message };
      }
      return { isValid: false, error: t('common.validation.invalidEmail') };
    }
  }, [emailLogin, emailTouched, emailSchema, t]);

  // Resend cooldown timer — must run regardless of view state
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendEmail = useCallback(async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: sentToEmail,
    });
    setLoading(false);
    if (error) {
      toast({
        title: t('common.toast.errorTitle'),
        description: t('auth.emailConfirmation.resendError'),
        variant: 'destructive',
      });
    } else {
      toast({
        title: t('auth.emailConfirmation.resendSuccess'),
        description: t('auth.emailConfirmation.resendSuccessDescription', { email: sentToEmail }),
      });
      setResendCooldown(60);
    }
  }, [sentToEmail, resendCooldown, t]);

  if (user && !isLoading) {
    navigate(resolvePostAuthPath({ isConsumer, isPrestador, needsOnboarding }));
    return null;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      emailSchema.parse(emailLogin);
      senhaSchema.parse(senhaLogin);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: t('common.toast.validationError'),
          description: error.errors[0].message,
          variant: 'destructive',
        });
        return;
      }
    }
    setLoading(true);
    const { error } = await signIn(emailLogin, senhaLogin);
    setLoading(false);
    if (error) {
      let mensagem = t('auth.loginError');
      if (error.message.includes('Invalid login credentials'))
        mensagem = t('auth.invalidCredentials');
      else if (error.message.includes('Email not confirmed'))
        mensagem = t('auth.emailNotConfirmed');
      toast({ title: t('common.toast.errorTitle'), description: mensagem, variant: 'destructive' });
    } else {
      toast({ title: t('auth.welcomeBack'), description: t('auth.loginSuccess') });
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      nomeSchema.parse(nomeSignup);
      emailSchema.parse(emailSignup);
      senhaSchema.parse(senhaSignup);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: t('common.toast.validationError'),
          description: error.errors[0].message,
          variant: 'destructive',
        });
        return;
      }
    }
    if (senhaSignup !== confirmaSenha) {
      toast({
        title: t('common.toast.errorTitle'),
        description: t('common.validation.passwordMismatch'),
        variant: 'destructive',
      });
      return;
    }
    track('signup_started');
    setLoading(true);
    const { error, needsEmailConfirmation } = await signUp(emailSignup, senhaSignup, nomeSignup);
    setLoading(false);
    if (error) {
      let mensagem = t('auth.signupError');
      if (error.message.includes('already registered'))
        mensagem = t('auth.alreadyRegistered');
      toast({ title: t('common.toast.errorTitle'), description: mensagem, variant: 'destructive' });
    } else if (needsEmailConfirmation) {
      track('signup_completed');
      setSentToEmail(emailSignup);
      setEmailSent(true);
    } else {
      track('signup_completed');
      toast({
        title: t('auth.accountCreated'),
        description: t('auth.accountCreatedDescription'),
      });
      navigate('/conta/escolher');
    }
  };

  // ─── Email-sent screen (signup confirmation pending) ───────────────────
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md ow-card p-8 flex flex-col items-center text-center gap-4">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
            <Mail className="h-8 w-8 text-foreground" />
          </div>
          <h2 className="text-2xl font-bold">{t('auth.emailConfirmation.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('auth.emailConfirmation.description')}{' '}
            <span className="font-medium text-foreground">{sentToEmail}</span>.{' '}
            {t('auth.emailConfirmation.instruction')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('auth.emailConfirmation.checkSpam')}
          </p>
          <div className="flex flex-col gap-2 w-full mt-2">
            <button
              type="button"
              className="ow-btn ow-btn--outline ow-btn--block"
              onClick={handleResendEmail}
              disabled={loading || resendCooldown > 0}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {resendCooldown > 0
                ? t('auth.emailConfirmation.resendCooldown', { seconds: resendCooldown })
                : t('auth.emailConfirmation.resend')}
            </button>
            <button
              type="button"
              className="ow-btn ow-btn--ghost ow-btn--block"
              onClick={() => {
                setEmailSent(false);
                setSentToEmail('');
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              {t('auth.emailConfirmation.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Loading (auth state being resolved) ───────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }

  const pwShow = senhaLogin.length > 0 && showPassword;
  const pwHide = senhaLogin.length > 0 && !showPassword;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* ─── Coluna esquerda: só personagens animados ──────────────────── */}
      <div className="relative hidden lg:flex flex-col justify-center items-center p-12 overflow-hidden bg-background">
        <div className="relative z-20 flex items-end justify-center" style={{ height: 500 }}>
          <AuthCharacters isTyping={isTyping} pwShow={pwShow} pwHide={pwHide} />
        </div>
      </div>

      {/* ─── Coluna direita: formulário ──────────────────────────────────── */}
      <div className="flex items-center justify-center p-8 bg-card">
        <div className="w-full" style={{ maxWidth: 420 }}>
          {/* Logo — visível em todas as telas, acima do heading */}
          <div className="flex justify-center mb-8">
            <Link to="/" className="ow-logo" aria-label="OS Works">
              <span className="ow-logo__mark">
                <img src="/bee.png" alt="" />
              </span>
              <span className="ow-logo__wm">
                <span className="os">os</span>works
              </span>
            </Link>
          </div>

          {/* Heading */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              {activeTab === 'login'
                ? t('auth.welcomeBack', 'Welcome back!')
                : t('auth.createAccount', 'Criar conta')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {activeTab === 'login'
                ? t('auth.enterDetails', 'Insira os seus dados')
                : t('auth.startFree', 'Comece grátis')}
            </p>
          </div>

          {/* ─── Login form ──────────────────────────── */}
          {activeTab === 'login' && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email-login">{t('auth.emailLabel')}</Label>
                <div className="relative">
                  <Input
                    id="email-login"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={emailLogin}
                    onChange={(e) => setEmailLogin(e.target.value)}
                    onFocus={() => setIsTyping(true)}
                    onBlur={() => {
                      setEmailTouched(true);
                      setIsTyping(false);
                    }}
                    autoComplete="email"
                    required
                    className={`h-12 rounded-2xl pr-10 ${
                      emailTouched && emailValidation.isValid === false
                        ? 'border-destructive'
                        : emailValidation.isValid === true
                          ? 'border-foreground'
                          : ''
                    }`}
                  />
                  {emailTouched && emailValidation.isValid === true && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground" />
                  )}
                  {emailTouched && emailValidation.isValid === false && (
                    <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                  )}
                </div>
                {emailTouched && emailValidation.error && (
                  <p className="text-xs text-destructive">{emailValidation.error}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="senha-login">{t('auth.passwordLabel')}</Label>
                <div className="relative">
                  <Input
                    id="senha-login"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={senhaLogin}
                    onChange={(e) => setSenhaLogin(e.target.value)}
                    onFocus={() => setIsTyping(true)}
                    onBlur={() => setIsTyping(false)}
                    autoComplete="current-password"
                    required
                    className="h-12 rounded-2xl pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? t('auth.hidePassword', 'Esconder senha') : t('auth.showPassword', 'Mostrar senha')}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox id="remember" />
                  <span className="text-sm font-normal">
                    {t('auth.rememberMe', 'Lembrar-me')}
                  </span>
                </label>
                <a
                  href="#"
                  className="text-sm font-medium hover:underline"
                  onClick={(e) => e.preventDefault()}
                >
                  {t('auth.forgotPassword', 'Esqueci a senha?')}
                </a>
              </div>

              <button
                type="submit"
                className="ow-btn ow-btn--primary ow-btn--block ow-btn--lg"
                disabled={loading}
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('auth.enter')}
              </button>
            </form>
          )}

          {/* ─── Signup form ──────────────────────────── */}
          {activeTab === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="nome-signup">{t('auth.nameLabel')}</Label>
                <Input
                  id="nome-signup"
                  placeholder={t('auth.namePlaceholder')}
                  value={nomeSignup}
                  onChange={(e) => setNomeSignup(e.target.value)}
                  onFocus={() => setIsTyping(true)}
                  onBlur={() => setIsTyping(false)}
                  autoComplete="name"
                  required
                  className="h-12 rounded-2xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-signup">{t('auth.emailLabel')}</Label>
                <Input
                  id="email-signup"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={emailSignup}
                  onChange={(e) => setEmailSignup(e.target.value)}
                  onFocus={() => setIsTyping(true)}
                  onBlur={() => setIsTyping(false)}
                  autoComplete="email"
                  required
                  className="h-12 rounded-2xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="senha-signup">{t('auth.passwordLabel')}</Label>
                <div className="relative">
                  <Input
                    id="senha-signup"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={senhaSignup}
                    onChange={(e) => setSenhaSignup(e.target.value)}
                    onFocus={() => setIsTyping(true)}
                    onBlur={() => setIsTyping(false)}
                    autoComplete="new-password"
                    required
                    className="h-12 rounded-2xl pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? t('auth.hidePassword', 'Esconder senha') : t('auth.showPassword', 'Mostrar senha')}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirma-senha">{t('auth.confirmPasswordLabel')}</Label>
                <Input
                  id="confirma-senha"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  value={confirmaSenha}
                  onChange={(e) => setConfirmaSenha(e.target.value)}
                  onFocus={() => setIsTyping(true)}
                  onBlur={() => setIsTyping(false)}
                  autoComplete="new-password"
                  required
                  className="h-12 rounded-2xl"
                />
              </div>
              <button
                type="submit"
                className="ow-btn ow-btn--primary ow-btn--block ow-btn--lg"
                disabled={loading}
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('auth.createAccount')}
              </button>
            </form>
          )}

          {/* Footer link — alterna entre tabs */}
          <div className="text-center text-sm text-muted-foreground mt-8">
            {activeTab === 'login' ? (
              <>
                {t('auth.noAccount', 'Não tens conta?')}{' '}
                <button
                  type="button"
                  className="font-medium text-foreground hover:underline"
                  onClick={() => setActiveTab('signup')}
                >
                  {t('auth.createAccount')}
                </button>
              </>
            ) : (
              <>
                {t('auth.hasAccount', 'Já tens conta?')}{' '}
                <button
                  type="button"
                  className="font-medium text-foreground hover:underline"
                  onClick={() => setActiveTab('login')}
                >
                  {t('auth.login')}
                </button>
              </>
            )}
          </div>

          {/* Termos + Privacidade */}
          <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground mt-6">
            <Link to="/privacidade" className="hover:text-foreground transition-colors">
              {t('landing.footer.privacy', 'Privacidade')}
            </Link>
            <a href="#" className="hover:text-foreground transition-colors">
              {t('landing.footer.terms', 'Termos')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
